import { z } from 'zod';
import { booleanQuerySchema, courseLevelSchema, csvArray, identifierSchema, matchedOnSchema, paginationQuerySchema, safeText, } from './common.js';
/**
 * Course shapes.
 *
 * Every response schema here is also the serialization filter: Fastify strips anything a schema
 * does not declare. That is a security control, not a convenience — it is the reason a column
 * added to `courses` later cannot leak through an endpoint that was never meant to expose it.
 */
/** The card. What a list, a search result, a pathway page and "similar courses" all render. */
export const courseSummarySchema = z.object({
    id: z.string(),
    isbn: z.string(),
    slug: z.string(),
    title: z.string(),
    subtitle: z.string().nullable(),
    authors: z.array(z.string()),
    durationSeconds: z.number().int().nullable(),
    levelMin: courseLevelSchema.nullable(),
    levelMax: courseLevelSchema.nullable(),
    /** 74 of the 186 licensed courses have none. They are still first-class catalog entries. */
    hasTranscripts: z.boolean(),
    summary: z.string().nullable(),
});
/** A summary plus why it was returned. Only search results carry the ranking fields. */
export const courseSearchResultSchema = courseSummarySchema.extend({
    score: z.number(),
    vectorRank: z.number().int().nullable(),
    lexicalRank: z.number().int().nullable(),
    matchedOn: matchedOnSchema,
});
export const lessonOutlineSchema = z.object({
    id: z.string(),
    lessonNo: z.number().int(),
    /** Null until enrichment has run. There are no human-readable titles in the source corpus. */
    title: z.string().nullable(),
    summary: z.string().nullable(),
    keyTopics: z.array(z.string()),
    durationSeconds: z.number().int(),
    isIntro: z.boolean(),
    position: z.number().int(),
});
export const moduleOutlineSchema = z.object({
    id: z.string(),
    moduleNo: z.number().int(),
    title: z.string().nullable(),
    summary: z.string().nullable(),
    position: z.number().int(),
    lessons: z.array(lessonOutlineSchema),
});
export const courseDetailSchema = courseSummarySchema.extend({
    description: z.string().nullable(),
    publisher: z.string().nullable(),
    durationRaw: z.string().nullable(),
    objectives: z.array(z.string()),
    mainSkills: z.array(z.string()),
    knowledgeSuite: z.string().nullable(),
    audience: z.string().nullable(),
    prerequisites: z.string().nullable(),
    outcomes: z.array(z.string()),
    topics: z.array(z.string()),
    /**
     * The model's own difficulty reading, kept separate from `levelMin`/`levelMax` because the
     * workbook's answer is the contractual one. A disagreement is information, not noise.
     */
    llmLevel: courseLevelSchema.nullable(),
    levelSource: z.enum(['workbook', 'llm', 'none']),
    moduleCount: z.number().int(),
    lessonCount: z.number().int(),
    /** Lesson titles and summaries only. Transcript text is never part of a course response. */
    modules: z.array(moduleOutlineSchema),
    skills: z.array(z.object({
        id: z.string(),
        name: z.string(),
        slug: z.string(),
        source: z.enum(['spreadsheet', 'llm']),
        weight: z.number(),
    })),
    sows: z.array(z.object({
        number: z.number().int(),
        label: z.string(),
        licenseQuantity: z.number().int().nullable(),
    })),
    pathways: z.array(z.object({
        id: z.string(),
        code: z.string(),
        name: z.string(),
        slug: z.string(),
        position: z.number().int(),
    })),
    similar: z.array(courseSummarySchema.extend({ similarity: z.number() })),
});
/**
 * The filter half of a catalog request, shared verbatim by `/courses` and `/search/courses` so
 * the two cannot interpret the same URL differently.
 */
export const courseFilterQuerySchema = z.object({
    level: csvArray(courseLevelSchema).optional(),
    hasTranscripts: booleanQuerySchema.optional(),
    minHours: z.coerce.number().min(0).max(1_000).optional(),
    maxHours: z.coerce.number().min(0).max(1_000).optional(),
    author: csvArray(safeText(200)).optional(),
    skill: csvArray(safeText(200)).optional(),
    sow: csvArray(z
        .string()
        .regex(/^[1-7]$/u, 'must be a SOW number from 1 to 7')
        .transform(Number)).optional(),
    knowledgeSuite: safeText(200).optional(),
    pathwayId: identifierSchema.optional(),
});
export const courseSortSchema = z.enum(['relevance', 'title', 'duration', 'recent']);
export const courseListQuerySchema = courseFilterQuerySchema.extend({
    ...paginationQuerySchema.shape,
    q: safeText(500).optional(),
    sort: courseSortSchema.default('relevance'),
});
export const facetBucketSchema = z.object({
    key: z.string(),
    label: z.string(),
    count: z.number().int(),
});
export const facetsSchema = z.object({
    /**
     * Courses matching the filters, **ignoring** any text query — the denominator the facet
     * counts are drawn from. The envelope's own `total` is the number of search results.
     */
    total: z.number().int(),
    levels: z.array(facetBucketSchema),
    durations: z.array(facetBucketSchema),
    authors: z.array(facetBucketSchema),
    skills: z.array(facetBucketSchema),
    sows: z.array(facetBucketSchema),
    knowledgeSuites: z.array(facetBucketSchema),
});
export const courseListResponseSchema = z.object({
    items: z.array(courseSearchResultSchema),
    total: z.number().int(),
    limit: z.number().int(),
    offset: z.number().int(),
    facets: facetsSchema,
    /**
     * True when the query could not be embedded and only the lexical arm ran. The UI says so
     * rather than quietly returning worse results.
     */
    degraded: z.boolean(),
});
/** Accepts a UUID, an ISBN-13 or a slug — the three identifiers a caller might plausibly hold. */
export const courseIdParamSchema = z.object({
    idOrIsbnOrSlug: identifierSchema,
});
export const courseSimilarQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(50).default(6),
});
export const courseSimilarResponseSchema = z.object({
    items: z.array(courseSummarySchema.extend({ similarity: z.number() })),
});
//# sourceMappingURL=course.js.map